import {truncate} from 'lodash';
import {cloneDeep} from 'lodash';
import {boardSlice} from '@reducers';
import {useDispatch, useSgfTree, useSelector} from '@utils';
import {UndoRedoManager} from '@utils/undoRedoManager';
import {
    a1ToPos,
    addMoveToCurrentNode,
    Analysis,
    calcMatAndMarkup,
    calcScoreDiffText,
    extractBoardSize,
    Ki,
    move,
    MoveInfo,
    offsetA1Move,
    round3,
    Sgf,
    TNode,
} from 'ghostban';
import React, {useRef} from 'react';
import {Table} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';

interface AnalysisInfoProps {
    normalizeRootInfoScore?: boolean;
    firstCorrectMoveAnalysis?: Analysis;
    analysis?: Analysis;
    showWinrate?: boolean;
    mat: number[][];
    markup: string[][];
    undoRedo: UndoRedoManager;
}

export const AnalysisInfo: React.FC<AnalysisInfoProps> = ({
    normalizeRootInfoScore,
    firstCorrectMoveAnalysis,
    analysis,
    showWinrate = true,
    undoRedo,
    mat,
    markup,
}) => {
    const currentAnalysis = analysis;
    const {t} = useTranslation();
    const rootInfo = analysis?.rootInfo;
    const currentPlayer = rootInfo?.currentPlayer || 'B';
    const dispatch = useDispatch();

    const {turn} = useSelector(s => s.board);
    const {root, currentNode} = useSgfTree();
    const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);

    const boardSize = root ? extractBoardSize(root) : 19;

    // TODO: duplicate code
    const saveHistory = (currentNode: TNode, replace = false) => {
        const sgf = new Sgf(root);
        const parsedSgfText = sgf.toSgf();
        if (replace) {
            undoRedo.replace(parsedSgfText, currentNode.model.id);
        } else {
            undoRedo.add(parsedSgfText, currentNode.model.id);
        }
    };

    const handlePv = (pv: string[], currentNode?: TNode): void => {
        resetBoard();
        if (!currentNode) return;

        setTimeout(() => {
            let pvTurn = turn;
            let node: TNode | undefined = currentNode;
            pv.forEach(v => {
                const {x, y} = a1ToPos(v);
                if (node) {
                    // Need re-calculate mat since the mat may be changed by pv preview
                    const {mat} = calcMatAndMarkup(node);
                    node = addMoveToCurrentNode(node, mat, x, y, pvTurn);
                }
                pvTurn = -pvTurn;
            });

            if (node) {
                dispatch(boardSlice.actions.setCurrentNodeId(node.model.id));
                saveHistory(node, false);
            }
        }, 100);
    };

    const handlePvPreview = (pv: string[]) => {
        let pvTurn = turn;
        let index = 0;
        let pvMat = cloneDeep(mat);
        let pvMarkup = cloneDeep(markup);
        intervalId.current = setInterval(() => {
            if (index < pv.length) {
                let newMat = cloneDeep(pvMat);
                const newMarkup = cloneDeep(pvMarkup);
                const v = pv[index];
                const {x, y} = a1ToPos(v);
                newMat = move(newMat, x, y, pvTurn);
                pvMat = newMat;
                if (pvMat[x][y] !== Ki.Empty) {
                    newMarkup[x][y] = (index + 1).toString();
                }
                pvMarkup = newMarkup;
                pvTurn = -pvTurn;
                dispatch(boardSlice.actions.setMat(newMat));
                dispatch(boardSlice.actions.setMarkup(newMarkup));
                index += 1;
            }
        }, 500);
    };

    const clearPvPreview = () => {
        resetBoard();
    };

    const resetBoard = () => {
        if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }
        if (currentNode) {
            const {mat, markup} = calcMatAndMarkup(currentNode);
            dispatch(boardSlice.actions.setMat(mat));
            dispatch(boardSlice.actions.setMarkup(markup));
        }
    };

    let rootInfoScore = `${rootInfo?.currentPlayer}${rootInfo?.scoreLead && rootInfo?.scoreLead > 0 ? `+${rootInfo?.scoreLead.toFixed(2)}` : rootInfo?.scoreLead.toFixed(2)}`;
    if (normalizeRootInfoScore) {
        if (firstCorrectMoveAnalysis?.rootInfo) {
            rootInfoScore = calcScoreDiffText(
                rootInfo,
                firstCorrectMoveAnalysis?.rootInfo
            );
        } else {
            rootInfoScore = '-';
        }
    }

    return (
        <>
            {rootInfo && (
                <div className="flex gap-2">
                    <div>
                        {t('analysis.current_player')}:{' '}
                        {rootInfo.currentPlayer}
                    </div>
                    {showWinrate && (
                        <div>
                            {t('analysis.winrate')}:{' '}
                            {round3(rootInfo.winrate, 100, 1)}%
                        </div>
                    )}
                    <div>
                        {t('analysis.visits')}: {rootInfo.visits}
                    </div>
                    <div>
                        {t('analysis.score')}: {rootInfoScore}
                    </div>
                </div>
            )}

            <Table
                size="sm"
                className="text-center"
                style={{
                    minWidth: '420px',
                }}>
                <thead>
                    <tr>
                        <th className="text-left">{t('analysis.order')}</th>
                        <th className="text-left">{t('analysis.move')}</th>
                        <th className="text-left">{t('analysis.prior')}</th>
                        {showWinrate && <th>{t('analysis.winrate')}</th>}
                        <th>{t('analysis.score')}</th>
                        <th>{t('analysis.visits')}</th>
                        <th>{t('analysis.pv')}</th>
                    </tr>
                </thead>
                <tbody>
                    {!currentAnalysis && (
                        <tr>
                            <td colSpan={7}>
                                {t('common.nothing_here_yet')}
                            </td>
                        </tr>
                    )}
                    {currentAnalysis?.moveInfos
                        .slice(0, 5)
                        .map((m: MoveInfo) => (
                            <tr key={m.move}>
                                <td style={{textAlign: 'left'}}>
                                    {m.order + 1}
                                </td>
                                <td style={{textAlign: 'left'}}>
                                    {offsetA1Move(m.move, 0, 19 - boardSize)}
                                </td>
                                <td style={{textAlign: 'left'}}>
                                    {round3(m.prior, 100, 1)}%
                                </td>
                                {showWinrate && (
                                    <td>
                                        <div className="inline-block px-1 py-0.5 bg-black text-white rounded-l-sm border border-black text-xs">
                                            {currentPlayer === 'B'
                                                ? round3(m.winrate, 100, 1)
                                                : round3(1 - m.winrate, 100, 1)}
                                            %
                                        </div>
                                        <div className="inline-block px-1 py-0.5 bg-white text-black rounded-r-sm border border-black text-xs">
                                            {currentPlayer === 'B'
                                                ? round3(1 - m.winrate, 100, 1)
                                                : round3(m.winrate, 100, 1)}
                                            %
                                        </div>
                                    </td>
                                )}
                                <td>
                                    {calcScoreDiffText(
                                        currentAnalysis.rootInfo,
                                        m
                                    )}
                                </td>
                                <td>{m.visits}</td>
                                <td className="text-left">
                                    <span
                                        className="cursor-pointer text-primary"
                                        onMouseUp={() => {
                                            handlePv(m.pv, currentNode);
                                        }}
                                        onMouseEnter={() =>
                                            handlePvPreview(m.pv)
                                        }
                                        onMouseLeave={() => clearPvPreview()}>
                                        {truncate(
                                            m.pv
                                                .map(move =>
                                                    offsetA1Move(
                                                        move,
                                                        0,
                                                        19 - boardSize
                                                    )
                                                )
                                                .join(' '),
                                            {
                                                length: 20,
                                                separator: ' ',
                                                omission: '...',
                                            }
                                        )}
                                    </span>
                                </td>
                            </tr>
                        ))}
                </tbody>
            </Table>
        </>
    );
};
